import { API_BASE_URL } from "../../../../config/api";
import type {
  AccountantPerformanceInsightsDTO,
  AccountantStatusSummaryDTO,
  MonthDetailBillsDTO,
  MonthDetailInvoicesDTO,
  MonthDetailTransactionsDTO,
  WorkHistoryItemDTO,
} from "../models/accountantStatusDto";

function qs(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(
    ([, value]) => value !== undefined && value !== null && value !== "",
  );
  if (entries.length === 0) return "";
  const searchParams = new URLSearchParams(
    entries.map(([key, value]) => [key, String(value)]),
  );
  return `?${searchParams.toString()}`;
}

async function fetchJson<T>(url: string, errorMessage: string): Promise<T> {
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const fallback = `${errorMessage}: ${response.status}`;
    const text = await response.text().catch(() => "");
    throw new Error(text || fallback);
  }

  return (await response.json()) as T;
}

export async function getAccountantStatusSummary(params: {
  year?: number;
  month?: number;
  period?: "last_month" | "last_3_months";
} = {}): Promise<AccountantStatusSummaryDTO> {
  const url = `${API_BASE_URL}/api/accountant-status/summary${qs(params)}`;
  return fetchJson<AccountantStatusSummaryDTO>(
    url,
    "Failed to fetch accountant status summary",
  );
}

export async function getAccountantMonthDetailBills(params: {
  year?: number;
  month?: number;
  limit?: number;
  offset?: number;
  period?: "last_month" | "last_3_months";
}): Promise<MonthDetailBillsDTO> {
  const url = `${API_BASE_URL}/api/accountant-status/month-detail-bills${qs(params)}`;
  return fetchJson<MonthDetailBillsDTO>(url, "Failed to fetch month detail bills");
}

export async function getAccountantMonthDetailInvoices(params: {
  year?: number;
  month?: number;
  limit?: number;
  offset?: number;
  period?: "last_month" | "last_3_months";
}): Promise<MonthDetailInvoicesDTO> {
  const url = `${API_BASE_URL}/api/accountant-status/month-detail-invoices${qs(params)}`;
  return fetchJson<MonthDetailInvoicesDTO>(
    url,
    "Failed to fetch month detail invoices",
  );
}

export async function getAccountantMonthDetailTransactions(params: {
  year?: number;
  month?: number;
  period?: "last_month" | "last_3_months";
}): Promise<MonthDetailTransactionsDTO> {
  const url = `${API_BASE_URL}/api/accountant-status/month-detail-transactions${qs(params)}`;
  return fetchJson<MonthDetailTransactionsDTO>(
    url,
    "Failed to fetch month detail transactions",
  );
}

export async function getAccountantWorkHistory(): Promise<WorkHistoryItemDTO[]> {
  const url = `${API_BASE_URL}/api/accountant-status/work-history`;
  return fetchJson<WorkHistoryItemDTO[]>(url, "Failed to fetch work history");
}

export async function getAccountantPerformanceInsights(): Promise<AccountantPerformanceInsightsDTO> {
  const url = `${API_BASE_URL}/api/accountant-status/performance-insights`;
  return fetchJson<AccountantPerformanceInsightsDTO>(
    url,
    "Failed to fetch accountant performance insights",
  );
}
